/* -----------------Helpers & Hooks--------------- */
import { useExamContext } from "@features/exam/contexts/ExamContext";
/* -----------------UI--------------- */
import Box from "@memorang/ui/components/Box";
import { CustomAppBar } from "@memorang/ui/components/CustomAppbar";
import Loader from "@memorang/ui/components/Loader";
import ScrollContainer from "@memorang/ui/components/ScrollContainer";

/* -----------------Types--------------- */
import type { PracticeTestExam } from "@memorang/types";

/* -----------------Child components--------------- */
import { PracticeTestsView } from "../components/PracticeTestsView";
import { NestedExamChipsContainer } from "../containers/NestedExamChipsContainer";

import { appBarMeta } from "@constants/appbar";
import { isWeb } from "@helpers/platform";
/* -----------------Helpers--------------- */
import { getPracticeTestsData } from "../helpers/practice-tests-data";

import { events } from "@constants/tracking";
import CollectUserDetailsDialog from "@features/account/components/dialogs/CollectUserDetailsDialog";
import { useAppStore } from "@hooks/useAppStore";
import useAuthUser from "@hooks/useAuthUser";
import { useDevice } from "@hooks/useDevice";
import { useGlobalStore } from "@hooks/useGlobalStore";
import { ConfirmDialog } from "@memorang/ui/components/ConfirmDialog";
import Container from "@memorang/ui/components/Container";
import { trackCustomEvent } from "analytics";
import { ErrorDialog } from "components/ErrorDialog";
import { useNavigation } from "expo-router";
import { OLXExamEligibilityDialog } from "../../../components/OLXExamEligibilityDialog";
/* -----------------Routing--------------- */
import { PurchaseDialog } from "../components/PurchaseDialog";
import { resumeSessionInfoDialogConfig } from "../constants";
import { usePracticeTests } from "../hooks/usePracticeTests";

const ResumeSessionInfoDialog = ({
	showResumeSessionInfoDialog,
	setShowResumeSessionInfoDialog,
	exam,
	creatingSession,
	handleActiveSession,
}: {
	showResumeSessionInfoDialog: boolean;
	setShowResumeSessionInfoDialog: (show: boolean) => void;
	exam: PracticeTestExam;
	creatingSession?: boolean;
	handleActiveSession: (examId: string, numericId?: number) => void;
}) => {
	const confirmDialogConfig = resumeSessionInfoDialogConfig({
		examName: exam.title,
		totalTime: exam.totalTime,
		timeElapsed: exam.timeElapsed,
	});
	return (
		<ConfirmDialog
			visible={showResumeSessionInfoDialog}
			dismissCallback={() => setShowResumeSessionInfoDialog(false)}
			title={confirmDialogConfig.title}
			body={confirmDialogConfig.body}
			icon="play-pause"
			confirmCallback={() => handleActiveSession(exam.id, exam.numericId)}
			confirmCtaLoading={creatingSession}
			confirmCta="Resume"
			dismissCta="Cancel"
		/>
	);
};

const PracticeTestsContainer = () => {
	const {
		currentExamContext: { examDate, examName },
	} = useExamContext();

	const {
		handlePracticeTestSession,
		selectedExamId,
		creatingSession,
		showPurchaseDialog,
		toggleShowPurchaseDialog,
		showErrorDialog,
		setShowErrorDialog,
		showCollectUserDetailsDialog,
		toggleShowCollectUserDetailsDialog,
		olxError,
		setOlxError,
		selectedNumericId,
		showResumeSessionInfoDialog,
		setShowResumeSessionInfoDialog,
		handleActiveSession,
		mixedItemDistResponse,
		loading,
	} = usePracticeTests();

	const { isAdmin } = useAuthUser();

	const showPaywall = useGlobalStore((state) => state.showPaywall);

	const practiceTestLabel = useAppStore(
		(state) => state.exam.practiceTest?.label,
	);
	const diagnosticLabel = useAppStore((state) => state.exam.diagnosticLabel);

	const finalHeaderTitle =
		appBarMeta["practice-tests"].title(practiceTestLabel);

	const { isMobile } = useDevice();
	const navigation = useNavigation<{ toggleDrawer?: () => void }>();

	const CustomLoader = () => {
		return (
			<Box flex={1}>
				<CustomAppBar
					title={finalHeaderTitle}
					navigation={navigation}
					handleTrackEvent={() => {
						trackCustomEvent({
							eventName: events.commandBarClicked,
							option: "command_bar",
						});
					}}
				/>
				<Container
					containerStyle={{
						height: "100%",
					}}
					maxWidth="sm"
				>
					<NestedExamChipsContainer
						disabled
						containerStyle={{
							paddingTop: 24,
							paddingHorizontal: isMobile ? 0 : 16,
						}}
					/>
					<Box
						flex={1}
						justifyContent="center"
						alignItems="center"
						height="100%"
					>
						<Loader />
					</Box>
				</Container>
			</Box>
		);
	};

	if (!mixedItemDistResponse || loading) {
		return <CustomLoader />;
	}

	const data = mixedItemDistResponse;

	const { sections, hasAccess: hasProductAccess } = getPracticeTestsData(
		data,
		examName,
		examDate,
		diagnosticLabel,
	);

	const Wrapper = isWeb ? ScrollContainer : Box;

	const selectedExamForResume = selectedNumericId
		? data?.exams?.find((exam) => exam.numericId === selectedNumericId)
		: undefined;

	return (
		<>
			<CustomAppBar
				title={finalHeaderTitle}
				navigation={navigation}
				handleTrackEvent={() => {
					trackCustomEvent({
						eventName: events.commandBarClicked,
						option: "command_bar",
					});
				}}
			/>
			<Wrapper
				style={{
					flexGrow: 1,
					paddingBottom: 24,
					paddingTop: 1,
				}}
				maxWidth="sm"
				// biome-ignore lint/a11y/useSemanticElements: <explanation>
				role="main"
			>
				<NestedExamChipsContainer
					containerStyle={{
						paddingVertical: 24,
						paddingHorizontal: isMobile ? 0 : 16,
					}}
				/>
				<PracticeTestsView
					sections={sections}
					handleNavigation={handlePracticeTestSession}
					selectedExamId={
						showResumeSessionInfoDialog ? undefined : selectedExamId
					}
					disableCard={creatingSession}
					hasAdminAccess={isAdmin && !showPaywall}
					hasProductAccess={hasProductAccess}
				/>
				{showPurchaseDialog && (
					<PurchaseDialog
						visible={showPurchaseDialog}
						handleClose={toggleShowPurchaseDialog}
						productType="PRACTICE_TEST"
						fromPracticeTestPage
					/>
				)}
				{showErrorDialog && (
					<ErrorDialog
						visible={showErrorDialog}
						handleClose={() => setShowErrorDialog(false)}
						title="Error"
						message="Session creation failed. Please try again."
					/>
				)}
				{!!olxError && (
					<OLXExamEligibilityDialog
						title={olxError.title}
						message={olxError.message}
						visible={!!olxError}
						handleClose={() => setOlxError(null)}
					/>
				)}

				{showCollectUserDetailsDialog && (
					<CollectUserDetailsDialog
						visible={showCollectUserDetailsDialog}
						handleClose={toggleShowCollectUserDetailsDialog}
					/>
				)}
				{showResumeSessionInfoDialog && selectedExamForResume && (
					<ResumeSessionInfoDialog
						showResumeSessionInfoDialog={showResumeSessionInfoDialog}
						setShowResumeSessionInfoDialog={setShowResumeSessionInfoDialog}
						exam={selectedExamForResume}
						creatingSession={creatingSession}
						handleActiveSession={handleActiveSession}
					/>
				)}
			</Wrapper>
		</>
	);
};

export default PracticeTestsContainer;
